import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StyledDataGrid from 'components/DataGridTable';
import { useMemo } from 'react';
import { getItemStatus } from '../../utils/inventorySearch';

const ItemsDataGrid = ({
  rows,
  loading,
  paginationModel,
  onPaginationModelChange,
  onEdit,
  onDelete,
  onQuickView,
}) => {
  // Preprocess rows to flatten category for filtering
  const processedRows = useMemo(() => {
    const processed = rows.map((row) => {
      let categoryName = '';
      if (row?.category) {
        if (typeof row.category === 'object') {
          categoryName = String(row.category.name || '').trim();
        } else {
          categoryName = String(row.category || '').trim();
        }
      }
      return {
        ...row,
        categoryName, // Add flattened categoryName field for filtering
      };
    });
    return processed;
  }, [rows]);
  const columns = [
    {
      field: 'name',
      headerName: 'Item',
      flex: 1,
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <Tooltip title="Click to quick edit" arrow>
          <Typography
            sx={{ cursor: 'pointer', color: 'black' }}
            onClick={(e) => onQuickView && onQuickView(e, params.row)}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'categoryName',
      headerName: 'Category',
      width: 150,
      sortable: true,
      filterable: true,
      valueGetter: (params) => {
        // In Material-UI DataGrid, valueGetter receives params object with { value, row }
        let actualRow, actualValue;

        if (params && typeof params === 'object' && 'row' in params) {
          // It's a params object: { value, row, field, ... }
          actualRow = params.row;
          actualValue = params.value;
        } else {
          // Fallback: assume params is the value and try to get row from context
          actualValue = params;
          actualRow = null;
        }

        // Use the value from the row if available, otherwise extract from category
        const categoryName = actualValue ?? actualRow?.categoryName;
        if (categoryName) {
          return String(categoryName).trim();
        }

        // Fallback: extract from category object
        if (actualRow?.category) {
          if (typeof actualRow.category === 'object') {
            return String(actualRow.category.name || '').trim();
          }
          return String(actualRow.category || '').trim();
        }

        return '';
      },
      renderCell: (params) => {
        const category = params.row?.category;
        if (!category) return '';
        if (typeof category === 'object') {
          return <Typography variant="body2">{category.name || ''}</Typography>;
        }
        return <Typography variant="body2">{category}</Typography>;
      },
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => row?.price || 0,
      renderCell: (params) => {
        const price = params.row?.price || 0;
        return `$${Number(price).toFixed(2)}`;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => {
        if (!row) return '';
        return getItemStatus(row);
      },
      renderCell: (params) => {
        if (!params.row) return null;
        const status = getItemStatus(params.row);
        let backgroundColor = '#00984F14';
        let textColor = '#00984F';

        if (status === 'Out of Stock') {
          backgroundColor = '#E2150814';
          textColor = '#E21508';
        } else if (status === 'Low Stock') {
          backgroundColor = '#FF8A0014';
          textColor = '#FF8A00';
        }

        return (
          <Chip
            label={status}
            size="small"
            variant="filled"
            sx={{
              backgroundColor: backgroundColor,
              color: textColor,
              '& .MuiChip-label': {
                color: textColor,
              },
            }}
          />
        );
      },
    },
    {
      field: 'stock',
      headerName: 'Quantity',
      width: 120,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => {
        return row?.inventoryInfo?.stock ?? 0;
      },
      renderCell: (params) => {
        const stock = params.row?.inventoryInfo?.stock ?? 0;
        return <Typography variant="body2">{stock}</Typography>;
      },
    },
    {
      field: 'barcodeNumber',
      headerName: 'Barcode',
      flex: 1,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => {
        const barcodes = row?.barcodeNumbers || [];
        return barcodes.length > 0 ? barcodes.join(', ') : '';
      },
      renderCell: (params) => {
        const barcodes = params.row?.barcodeNumbers || [];

        if (!barcodes || barcodes.length === 0) {
          return <Typography variant="body2">N/A</Typography>;
        }

        return (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              overflowX: 'auto',
              maxWidth: 300,
              '&::-webkit-scrollbar': {
                height: 1,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: 3,
              },
            }}
          >
            {barcodes.map((barcode, index) => (
              <Chip
                key={index}
                label={barcode}
                size="small"
                sx={{
                  flexShrink: 0,
                  fontSize: '14px',
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'sku',
      headerName: 'SKU',
      width: 200,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => {
        return row?.inventoryInfo?.sku || '';
      },
      renderCell: (params) => {
        const sku = params.row?.inventoryInfo?.sku || '';
        const displayText = sku || 'N/A';
        return (
          <Tooltip title={sku || 'N/A'} arrow>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {displayText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'unit',
      headerName: 'Unit of Measure',
      width: 150,
      sortable: true,
      filterable: true,
      valueGetter: (value, row) => {
        const unit = row?.inventoryInfo?.unit || '';
        return unit ? unit.charAt(0).toUpperCase() + unit.slice(1) : '';
      },
      renderCell: (params) => {
        const unit = params.row?.inventoryInfo?.unit || '';
        const formattedUnit = unit
          ? unit.charAt(0).toUpperCase() + unit.slice(1)
          : 'N/A';
        return <Typography variant="body2">{formattedUnit}</Typography>;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => onEdit && onEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => onDelete && onDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
      <StyledDataGrid
        rows={processedRows}
        columns={columns}
        getRowId={(row) => row._id}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        disableSelectionOnClick
        loading={loading}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        filterMode="client"
        hideToolbar={true}
      />
    </Box>
  );
};

export default ItemsDataGrid;
